import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApiService } from '../../services/adminApi';
import { AdminErrorGroup } from '../../types/admin';
import { compareRowValues, SortOrder, TableCard, TableHeader, TableViewport } from '../table/TableKit';
import {
  EmptyRow,
  ErrorNotice,
  LoadingSpinner,
  SortableTh,
  formatNumber,
  tdClass,
} from './shared';
import RelativeTime from '../RelativeTime';
import { urlEnum, useUrlState } from '../../hooks/useUrlState';

const ERROR_SORT_KEYS = ['message', 'count', 'instance_count', 'last_seen'] as const;
type ErrorSort = typeof ERROR_SORT_KEYS[number];

const URL_SPEC = {
  'jobs.sort': urlEnum(ERROR_SORT_KEYS, 'count'),
  'jobs.order': urlEnum(['asc', 'desc'] as const, 'desc'),
  'api.sort': urlEnum(ERROR_SORT_KEYS, 'count'),
  'api.order': urlEnum(['asc', 'desc'] as const, 'desc'),
  'cli.sort': urlEnum(ERROR_SORT_KEYS, 'count'),
  'cli.order': urlEnum(['asc', 'desc'] as const, 'desc'),
};

function ErrorGroupTable({
  title,
  groups,
  sort,
  order,
  onSort,
}: {
  title: string;
  groups: AdminErrorGroup[];
  sort: ErrorSort;
  order: SortOrder;
  onSort: (sort: string, order: SortOrder) => void;
}) {
  const sorted = useMemo(() => {
    const dir = order === 'asc' ? 1 : -1;
    return [...groups].sort((a, b) => dir * compareRowValues(a[sort], b[sort]));
  }, [groups, sort, order]);

  return (
    <TableCard className="mb-6">
      <TableHeader title={title} />
      <TableViewport>
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-700">
              <SortableTh label="Error Message" sortKey="message" sort={sort} order={order} onSort={onSort} firstOrder="asc" />
              <SortableTh label="Count" sortKey="count" sort={sort} order={order} onSort={onSort} />
              <SortableTh label="Instances" sortKey="instance_count" sort={sort} order={order} onSort={onSort} />
              <SortableTh label="Last Seen" sortKey="last_seen" sort={sort} order={order} onSort={onSort} />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {sorted.map((g) => (
              <tr key={g.message}>
                <td className={`${tdClass} max-w-2xl`}>
                  <span className="line-clamp-2" title={g.message}>{g.message}</span>
                </td>
                <td className={tdClass}>{formatNumber(g.count)}</td>
                <td className={tdClass}>{formatNumber(g.instance_count)}</td>
                <td className={tdClass}><RelativeTime value={g.last_seen} /></td>
              </tr>
            ))}
            {groups.length === 0 && <EmptyRow colSpan={4} text="No errors in the last 24 hours. 🎉" />}
          </tbody>
        </table>
      </TableViewport>
    </TableCard>
  );
}

function AdminErrors() {
  const [urlState, setUrlState] = useUrlState(URL_SPEC);
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'errors'],
    queryFn: () => adminApiService.getErrors(),
    retry: false,
  });

  return (
    <div>
      <h2 className="text-xl font-bold text-heading mb-1">Errors</h2>
      <p className="text-sm text-secondary mb-4">Last 24 hours.</p>

      {error && <ErrorNotice message="Failed to load errors." />}
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          <ErrorGroupTable
            title="Failed Jobs"
            groups={data?.jobs ?? []}
            sort={urlState['jobs.sort']}
            order={urlState['jobs.order']}
            onSort={(sort, order) => setUrlState({ 'jobs.sort': sort as ErrorSort, 'jobs.order': order })}
          />
          <ErrorGroupTable
            title="API Conversion Errors"
            groups={data?.api ?? []}
            sort={urlState['api.sort']}
            order={urlState['api.order']}
            onSort={(sort, order) => setUrlState({ 'api.sort': sort as ErrorSort, 'api.order': order })}
          />
          <ErrorGroupTable
            title="CLI Operation Failures"
            groups={data?.cli ?? []}
            sort={urlState['cli.sort']}
            order={urlState['cli.order']}
            onSort={(sort, order) => setUrlState({ 'cli.sort': sort as ErrorSort, 'cli.order': order })}
          />
        </>
      )}
    </div>
  );
}

export default AdminErrors;
